import { supabase } from '../config/supabase';
import { getMedicalRecordAttachmentsObject } from './medicalRecordStatus';

export interface MedicalRecordAuthorInfo {
  userId: string | null;
  name: string;
  role: string | null;
  professionalTitle: string;
  councilNumber: string | null;
}

interface BuildMedicalRecordAuthorInfoInput {
  userId?: string | null;
  name?: string | null;
  role?: string | null;
  crm?: string | null;
  crefito?: string | null;
  councilNumber?: string | null;
}

const normalizeOptionalString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;

  const trimmedValue = value.trim();
  return trimmedValue ? trimmedValue : null;
};

const isPlaceholderAuthorName = (value?: string | null): boolean =>
  !normalizeOptionalString(value) || normalizeOptionalString(value) === 'Profissional Responsável';

const resolveMedicalRecordAuthorRole = (
  role?: string | null,
  crm?: string | null,
  crefito?: string | null,
): string | null => {
  if (role === 'doctor' || role === 'fisioterapeuta' || role === 'admin' || role === 'interno') {
    return role;
  }

  if (crm) return 'doctor';
  if (crefito) return 'fisioterapeuta';

  return role ?? null;
};

const resolveProfessionalTitle = (role?: string | null): string => {
  if (role === 'doctor') return 'Médico Responsável';
  if (role === 'fisioterapeuta') return 'Fisioterapeuta Responsável';
  if (role === 'admin') return 'Administrador Responsável';
  if (role === 'interno') return 'Interno Responsável';
  return 'Profissional Responsável';
};

export const requiresProfessionalRegistration = (role?: string | null): boolean =>
  role === 'doctor' || role === 'fisioterapeuta';

export const hasNonApplicableProfessionalRegistration = (role?: string | null): boolean =>
  role === 'admin' || role === 'interno';

const resolveCouncilNumber = (
  role?: string | null,
  crm?: string | null,
  crefito?: string | null,
  councilNumber?: string | null,
): string | null => {
  if (councilNumber && councilNumber.trim()) {
    return councilNumber.trim();
  }

  if (role === 'doctor') {
    return crm?.trim() || null;
  }

  if (role === 'fisioterapeuta') {
    return crefito?.trim() || null;
  }

  if (role === 'interno') {
    return null;
  }

  return crm?.trim() || crefito?.trim() || null;
};

export const buildMedicalRecordAuthorInfo = (
  input: BuildMedicalRecordAuthorInfoInput,
): MedicalRecordAuthorInfo => {
  const resolvedRole = resolveMedicalRecordAuthorRole(input.role, input.crm, input.crefito);

  return {
    userId: input.userId ?? null,
    name: input.name?.trim() || 'Profissional Responsável',
    role: resolvedRole,
    professionalTitle: resolveProfessionalTitle(resolvedRole),
    councilNumber: resolveCouncilNumber(
      resolvedRole,
      input.crm,
      input.crefito,
      input.councilNumber,
    ),
  };
};

export const buildMedicalRecordAuthorSnapshot = (
  input: BuildMedicalRecordAuthorInfoInput,
): MedicalRecordAuthorInfo => buildMedicalRecordAuthorInfo(input);

export const getMedicalRecordAuthorFromAttachments = (
  recordAttachments: unknown,
): MedicalRecordAuthorInfo | null => {
  const attachments = getMedicalRecordAttachmentsObject(recordAttachments);
  const rawSnapshot = attachments.authorSnapshot;

  if (!rawSnapshot || typeof rawSnapshot !== 'object' || Array.isArray(rawSnapshot)) {
    return null;
  }

  const snapshot = rawSnapshot as Record<string, unknown>;
  const userId = normalizeOptionalString(snapshot.userId);
  const name = normalizeOptionalString(snapshot.name);
  const role = normalizeOptionalString(snapshot.role);
  const councilNumber = normalizeOptionalString(snapshot.councilNumber);

  if (!userId && !name && !role && !councilNumber) {
    return null;
  }

  return buildMedicalRecordAuthorInfo({
    userId,
    name,
    role,
    councilNumber,
  });
};

export const mergeMedicalRecordAuthorInfo = (
  primary: MedicalRecordAuthorInfo | null,
  fallback: MedicalRecordAuthorInfo | null,
): MedicalRecordAuthorInfo | null => {
  if (!primary) return fallback;
  if (!fallback) return primary;

  const name = isPlaceholderAuthorName(primary.name)
    ? fallback.name
    : primary.name;

  return buildMedicalRecordAuthorInfo({
    userId: primary.userId ?? fallback.userId,
    name,
    role: primary.role ?? fallback.role,
    councilNumber: primary.councilNumber ?? fallback.councilNumber,
  });
};

export const fetchMedicalRecordAuthors = async (
  userIds: string[],
  clientId: string,
): Promise<Map<string, MedicalRecordAuthorInfo>> => {
  const uniqueUserIds = [...new Set(userIds.filter(Boolean))];

  if (!uniqueUserIds.length || !clientId) {
    return new Map();
  }

  const [{ data: usersData, error: usersError }, { data: rolesData, error: rolesError }] =
    await Promise.all([
      supabase
        .from('users')
        .select(`
          userId,
          userDisplayName,
          doctors(
            doctorCrm,
            doctorCrefito
          )
        `)
        .in('userId', uniqueUserIds),
      supabase
        .from('user_clients')
        .select('userId, userRole')
        .eq('clientId', clientId)
        .in('userId', uniqueUserIds),
    ]);

  if (usersError) {
    throw usersError;
  }

  if (rolesError) {
    throw rolesError;
  }

  const roleByUserId = new Map<string, string | null>();
  for (const roleRow of rolesData ?? []) {
    if (!roleRow?.userId) continue;
    roleByUserId.set(roleRow.userId, roleRow.userRole ?? null);
  }

  const authorDirectory = new Map<string, MedicalRecordAuthorInfo>();
  for (const row of usersData ?? []) {
    if (!row?.userId) continue;

    const doctorData = Array.isArray(row.doctors) ? row.doctors[0] : row.doctors;
    authorDirectory.set(
      row.userId,
      buildMedicalRecordAuthorInfo({
        userId: row.userId,
        name: row.userDisplayName ?? null,
        role: roleByUserId.get(row.userId) ?? null,
        crm: doctorData?.doctorCrm ?? null,
        crefito: doctorData?.doctorCrefito ?? null,
      }),
    );
  }

  return authorDirectory;
};
